import React, { useCallback, useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { Link } from "react-router-dom";
import MenuBar from "../../components/MenuBar";
import HeaderBar from "../../components/HeaderBar";
import {
  listCctvCameras,
  upsertCctvCamera,
  deleteCctvCamera,
  refreshCctvCameraHealth,
  discoverCctvOnvif,
} from "../../services/cctvApi";

const emptyForm = () => ({
  id: "",
  name: "",
  ipAddress: "",
  username: "admin",
  password: "",
  location: "",
  branch: "",
  channel: 1,
  subtype: 1,
  rtspPort: 554,
  httpPort: 80,
  serverPort: 8000,
  vendor: "hikvision",
  rtspUrl: "",
  ptzEnabled: false,
  recordingEnabled: false,
  motionDetectionEnabled: false,
  gridOrder: 0,
});

function formatCallableError(err) {
  const code = String(err?.code || "").replace(/^functions\//, "");
  const msg = String(err?.message || err?.details || "Request failed").trim();
  if (code && code !== "internal") return `${msg} (${code})`;
  if (code === "internal")
    return `${msg || "Internal error"} — check Firebase Functions logs (CCTV_ENCRYPTION_KEY / permission).`;
  return msg;
}

export default function CctvCamerasPage() {
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listCctvCameras();
      setCameras(res?.data?.cameras || []);
    } catch (e) {
      toast.error(e?.message || "Failed to load cameras");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (cam) => {
    setForm({
      id: cam.id,
      name: cam.name || "",
      ipAddress: cam.ipAddress || "",
      username: cam.username || "admin",
      password: "",
      location: cam.location || "",
      branch: cam.branch || "",
      channel: cam.channel ?? 1,
      subtype: cam.subtype ?? 0,
      rtspPort: cam.rtspPort ?? 554,
      httpPort: cam.httpPort ?? 80,
      serverPort: cam.serverPort ?? 8000,
      vendor: cam.vendor || "cpplus",
      rtspUrl: cam.rtspUrl || "",
      ptzEnabled: !!cam.ptzEnabled,
      recordingEnabled: !!cam.recordingEnabled,
      motionDetectionEnabled: !!cam.motionDetectionEnabled,
      gridOrder: cam.gridOrder ?? 0,
    });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const camera = {
        name: form.name.trim(),
        ipAddress: form.ipAddress.trim(),
        username: form.username.trim(),
        location: form.location.trim(),
        branch: form.branch.trim(),
        channel: Number(form.channel) || 1,
        subtype: Number(form.subtype) === 1 ? 1 : 0,
        rtspPort: Number(form.rtspPort) || 554,
        httpPort: Number(form.httpPort) || 80,
        serverPort: Number(form.serverPort) || 0,
        vendor: form.vendor,
        rtspUrl: form.rtspUrl.trim(),
        ptzEnabled: form.ptzEnabled,
        recordingEnabled: form.recordingEnabled,
        motionDetectionEnabled: form.motionDetectionEnabled,
        gridOrder: Number(form.gridOrder) || 0,
      };
      if (form.password) camera.password = form.password;
      await upsertCctvCamera({ id: form.id || undefined, camera });
      toast.success(form.id ? "Camera updated" : "Camera added");
      setShowForm(false);
      load();
    } catch (err) {
      console.error("upsertCctvCamera", err);
      toast.error(formatCallableError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete camera "${name}"?`)) return;
    try {
      await deleteCctvCamera({ cameraId: id });
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.message || "Delete failed");
    }
  };

  const runHealth = async () => {
    try {
      const res = await refreshCctvCameraHealth();
      toast.success(`Health check: ${res?.data?.checked ?? 0} camera(s)`);
      load();
    } catch (e) {
      toast.error(e?.message || "Health check failed (is gateway running?)");
    }
  };

  const runDiscover = async () => {
    try {
      const res = await discoverCctvOnvif({ subnet: "" });
      const n = (res?.data?.devices || []).length;
      toast.success(n ? `Found ${n} device(s)` : res?.data?.message || "No devices found");
    } catch (e) {
      toast.error(e?.message || "Discovery failed");
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar title="CCTV — Cameras" />

      <div className="mx-auto max-w-5xl px-3 py-4">
        <div className="mb-3 flex flex-wrap gap-2">
          <button type="button" onClick={openNew} className="rounded bg-red-700 px-3 py-1.5 text-xs font-bold hover:bg-red-600">
            Add camera
          </button>
          <button type="button" onClick={runHealth} className="rounded bg-slate-800 px-3 py-1.5 text-xs font-semibold hover:bg-slate-700">
            Health check all
          </button>
          <button type="button" onClick={runDiscover} className="rounded bg-slate-800 px-3 py-1.5 text-xs font-semibold hover:bg-slate-700">
            ONVIF discover
          </button>
          <Link to="/cctv" className="rounded bg-slate-800 px-3 py-1.5 text-xs font-semibold hover:bg-slate-700">
            Live view
          </Link>
          <Link to="/tools/cctv-settings" className="rounded bg-slate-800 px-3 py-1.5 text-xs font-semibold hover:bg-slate-700">
            Settings
          </Link>
        </div>

        {loading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-800">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-900 text-xs uppercase text-slate-400">
                <tr>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">IP</th>
                  <th className="px-3 py-2">Branch</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">PTZ</th>
                  <th className="px-3 py-2">Order</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {cameras.map((c) => (
                  <tr key={c.id} className="border-t border-slate-800 hover:bg-slate-900/50">
                    <td className="px-3 py-2 font-medium">{c.name}</td>
                    <td className="px-3 py-2 text-slate-300">{c.ipAddress || "—"}</td>
                    <td className="px-3 py-2 text-slate-300">{c.branch || "—"}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                          c.status === "online" ? "bg-emerald-800 text-emerald-100" : "bg-slate-700 text-slate-300"
                        }`}
                      >
                        {c.status || "unknown"}
                      </span>
                    </td>
                    <td className="px-3 py-2">{c.ptzEnabled ? "Yes" : "—"}</td>
                    <td className="px-3 py-2">{c.gridOrder ?? 0}</td>
                    <td className="px-3 py-2 text-right">
                      <button type="button" className="mr-2 text-xs text-sky-400 hover:underline" onClick={() => openEdit(c)}>
                        Edit
                      </button>
                      <button type="button" className="text-xs text-rose-400 hover:underline" onClick={() => remove(c.id, c.name)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {cameras.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center text-slate-500">
                      No cameras yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {showForm && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-4">
            <form
              onSubmit={save}
              className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-slate-700 bg-slate-900 p-4 shadow-xl"
            >
              <h2 className="mb-3 text-lg font-bold">{form.id ? "Edit camera" : "New camera"}</h2>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <label className="col-span-2">
                  <span className="mb-1 block text-xs text-slate-400">Name *</span>
                  <input
                    className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    required
                  />
                </label>
                <label>
                  <span className="mb-1 block text-xs text-slate-400">IP address</span>
                  <input
                    className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5"
                    value={form.ipAddress}
                    onChange={(e) => setForm((f) => ({ ...f, ipAddress: e.target.value }))}
                  />
                </label>
                <label>
                  <span className="mb-1 block text-xs text-slate-400">Username</span>
                  <input
                    className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5"
                    value={form.username}
                    onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                  />
                </label>
                <div className="col-span-2 rounded border border-amber-800/60 bg-amber-950/40 p-2 text-xs text-amber-100">
                  <strong>Dahua / CP Plus</strong> — open <code className="text-amber-200">http://192.168.29.2</code>, login,
                  then <strong>Setup → Network → RTSP</strong> (enable + copy URL). Test in VLC on port{" "}
                  <strong>554</strong>:{" "}
                  <code className="break-all text-amber-200">
                    rtsp://WINS:Wins@327@192.168.29.2:554/Streaming/Channels/101
                  </code>
                  . When VLC plays, paste that URL in <strong>Full RTSP URL</strong>.
                </div>
                <label className="col-span-2">
                  <span className="mb-1 block text-xs text-slate-400">
                    Password {form.id ? "(leave blank to keep)" : "*"}
                  </span>
                  <input
                    type="password"
                    className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5"
                    value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  />
                </label>
                <label>
                  <span className="mb-1 block text-xs text-slate-400">RTSP port</span>
                  <input
                    type="number"
                    className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5"
                    value={form.rtspPort}
                    onChange={(e) => setForm((f) => ({ ...f, rtspPort: e.target.value }))}
                  />
                  <span className="mt-0.5 block text-[10px] text-slate-500">Use 554 for live stream (not server 8000).</span>
                </label>
                <label>
                  <span className="mb-1 block text-xs text-slate-400">HTTP port</span>
                  <input
                    type="number"
                    className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5"
                    value={form.httpPort}
                    onChange={(e) => setForm((f) => ({ ...f, httpPort: e.target.value }))}
                  />
                  <span className="mt-0.5 block text-[10px] text-slate-500">Browser: http://IP:80</span>
                </label>
                <label>
                  <span className="mb-1 block text-xs text-slate-400">Server port (app)</span>
                  <input
                    type="number"
                    className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5"
                    value={form.serverPort}
                    onChange={(e) => setForm((f) => ({ ...f, serverPort: e.target.value }))}
                  />
                  <span className="mt-0.5 block text-[10px] text-slate-500">
                    CP Plus mobile app port — not RTSP. Close VLC before Live view (one RTSP client).
                  </span>
                </label>
                <label>
                  <span className="mb-1 block text-xs text-slate-400">Channel</span>
                  <input
                    type="number"
                    className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5"
                    value={form.channel}
                    onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value }))}
                  />
                </label>
                <label>
                  <span className="mb-1 block text-xs text-slate-400">Subtype (0=main/101, 1=sub/102)</span>
                  <input
                    type="number"
                    className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5"
                    value={form.subtype}
                    onChange={(e) => setForm((f) => ({ ...f, subtype: e.target.value }))}
                  />
                </label>
                <label>
                  <span className="mb-1 block text-xs text-slate-400">Vendor / RTSP style</span>
                  <select
                    className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5"
                    value={form.vendor}
                    onChange={(e) => setForm((f) => ({ ...f, vendor: e.target.value }))}
                  >
                    <option value="cpplus">CP Plus / Hikvision (Streaming/Channels)</option>
                    <option value="hikvision">Hikvision</option>
                    <option value="dahua">Dahua / realmonitor</option>
                    <option value="onvif">Generic /stream1</option>
                  </select>
                </label>
                <label className="col-span-2">
                  <span className="mb-1 block text-xs text-slate-400">
                    Full RTSP URL (paste exact VLC URL — best if password has $ or @)
                  </span>
                  <input
                    className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5 font-mono text-xs"
                    placeholder="rtsp://WINS:Wins@327@192.168.29.2:554/Streaming/Channels/102"
                    value={form.rtspUrl}
                    onChange={(e) => setForm((f) => ({ ...f, rtspUrl: e.target.value }))}
                  />
                </label>
                <label>
                  <span className="mb-1 block text-xs text-slate-400">Branch</span>
                  <input
                    className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5"
                    value={form.branch}
                    onChange={(e) => setForm((f) => ({ ...f, branch: e.target.value }))}
                  />
                </label>
                <label>
                  <span className="mb-1 block text-xs text-slate-400">Location</span>
                  <input
                    className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5"
                    value={form.location}
                    onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                  />
                </label>
                <label>
                  <span className="mb-1 block text-xs text-slate-400">Grid order</span>
                  <input
                    type="number"
                    className="w-full rounded border border-slate-700 bg-slate-950 px-2 py-1.5"
                    value={form.gridOrder}
                    onChange={(e) => setForm((f) => ({ ...f, gridOrder: e.target.value }))}
                  />
                </label>
                <label className="flex items-center gap-2 pt-5">
                  <input
                    type="checkbox"
                    checked={form.ptzEnabled}
                    onChange={(e) => setForm((f) => ({ ...f, ptzEnabled: e.target.checked }))}
                  />
                  <span className="text-xs">PTZ enabled</span>
                </label>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  className="rounded bg-slate-800 px-3 py-1.5 text-xs font-semibold"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded bg-red-700 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
